/**
 * NIGHT: the same city after dark (Karolis 2026-10-17: "a light mode and a dark mode where they would drive the car at
 * night … the lights and signs would look sick"). Day stays exactly as it is: every day value is captured from the scene
 * when NightMode is made and put back on `set("day")`; every night-only shader term is multiplied by a uniform that is 0
 * by day.
 *
 * What night changes — and what it costs (the iGPU budget, PLAYBOOK § 6): no real lights are added, no per-pixel lights.
 *  · sky: a blue-violet gradient, stars and a moon (the sky shader's own `night` branch, 0 draws); darker, nearer fog;
 *    moonlight in place of the sun (same direction: the shadow box is unchanged), a cool low sky light, a night
 *    environment map for the PBR props (made once, on the first night).
 *  · the city's own glass and lamps glow: the Kenney kits' window, shop-front and lamp-lens triangles carry a
 *    `nightGlow` vertex attribute (a seed per window), the kit materials add a warm emission for ~40 % of the windows,
 *    chosen per window and per building (0 draws: it is the city's own BatchedMesh).
 *  · street lamps: one InstancedMesh for every lamp's light pool (road + sidewalk), its cone of light and its halo (1 draw).
 *  · everything that moves: one InstancedMesh of soft quads — the roadster's headlight pools and tail lights, the
 *    thrusters, ETH trucks' tail lights and oncoming headlights, ELON's flashing light bar and hazards, the billboards'
 *    lamps (1 draw).
 *  · billboards: neon brighter, boards washed by their own lamps (a uniform in their shared material).
 * On low (no post, no bloom): no light cones under the lamps, smaller halos.
 */
import * as THREE from "three";
import type { WorldEntity } from "../data/model";

export type TimeOfDay = "day" | "night";
export type TimeSetting = TimeOfDay | "auto";

/** "Auto": night from 20:00 until 06:00, local time. */
export const NIGHT_FROM_HOUR = 20;
export const NIGHT_UNTIL_HOUR = 6;

/** The look a setting gives at `now` (Auto: night between 20:00 and 06:00 local time). Unknown values are day. */
export function resolveTimeOfDay(setting: TimeSetting | string | undefined, now: Date = new Date()): TimeOfDay {
  if (setting === "night") return "night";
  if (setting === "auto") {
    const hour = now.getHours();
    return hour >= NIGHT_FROM_HOUR || hour < NIGHT_UNTIL_HOUR ? "night" : "day";
  }
  return "day";
}

/** Dev switches `?night` / `?day` (screenshots): they win over the saved setting. */
export function timeOfDaySwitch(search: string): TimeOfDay | undefined {
  const params = new URLSearchParams(search);
  if (params.has("night")) return "night";
  if (params.has("day")) return "day";
  return undefined;
}

// ------------------------------------------------------------------------------------------------ the city's own lights

/** Palette cells (16 × 16 grid over the Kenney colour maps) that are glass or lamp lenses, per kit. */
const GLASS_CELLS = (u: number, v: number) => u >= 10 && u <= 11 && v >= 4 && v <= 7;   // the blue glass swatches
const SHOP_CELLS = (u: number, v: number) => u === 3 && v >= 8 && v <= 11;              // commercial: the dark shop fronts
const LENS_CELL = (u: number, v: number) => u === 8 && v === 10;                        // roads: the lamp's white lens

/** What a kit triangle is at night. The industrial kit's colour map is laid out differently (its "blue" cells are orange):
 *  there only the glossy "-specular" material is glass. */
function glowKind(material: string, u: number, v: number): "window" | "shop" | "lens" | undefined {
  const cu = Math.floor(THREE.MathUtils.clamp(u, 0, 0.9999) * 16), cv = Math.floor(THREE.MathUtils.clamp(v, 0, 0.9999) * 16);
  if (material.includes("roads")) return LENS_CELL(cu, cv) ? "lens" : undefined;
  if (material.includes("industrial")) return material.includes("specular") ? "window" : undefined;
  if (GLASS_CELLS(cu, cv)) return "window";
  if (material.includes("commercial") && SHOP_CELLS(cu, cv)) return "shop";
  return undefined;
}

const hash3 = (x: number, y: number, z: number): number => {
  let h = (Math.imul(x | 0, 73856093) ^ Math.imul(y | 0, 19349663) ^ Math.imul(z | 0, 83492791)) >>> 0;
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d) >>> 0;
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
};

/**
 * A copy of a kit geometry with the `nightGlow` attribute: every window pane (its triangles joined by shared vertices,
 * panes of one window joined by a 0.25-unit cell) gets one seed, so a window is lit or dark as a whole. Encoding: 0 none ·
 * (0, 1) a window · (−1, 0) a shop front · 2 a lamp lens · (3, 4) / (−4, −3) a BIG glass or shop panel (a skyscraper's
 * whole curtain wall is one quad) — the shader draws a grid of panes on those. Kits with no glass get zeros
 * (BatchedMesh needs the same attributes on every geometry of a batch).
 */
export function withNightGlow(geometry: THREE.BufferGeometry, materialName: string): THREE.BufferGeometry {
  const out = geometry.clone();
  const uv = geometry.getAttribute("uv") as THREE.BufferAttribute | undefined;
  const pos = geometry.getAttribute("position") as THREE.BufferAttribute;
  const glow = new Float32Array(pos.count);
  const index = geometry.index;
  if (uv && index) {
    const kit = materialName;
    const parent = new Int32Array(pos.count).map((_, i) => i);
    const find = (i: number): number => { while (parent[i] !== i) { parent[i] = parent[parent[i]]; i = parent[i]; } return i; };
    const kinds = new Map<number, "window" | "shop" | "lens">();
    for (let t = 0; t < index.count; t += 3) {
      const a = index.getX(t), b = index.getX(t + 1), c = index.getX(t + 2);
      const kind = glowKind(kit, (uv.getX(a) + uv.getX(b) + uv.getX(c)) / 3, (uv.getY(a) + uv.getY(b) + uv.getY(c)) / 3);
      if (!kind) continue;
      for (const v of [a, b, c]) kinds.set(v, kind);
      const ra = find(a);
      parent[find(b)] = ra;
      parent[find(c)] = ra;
    }
    const centre = new Map<number, { x: number; y: number; z: number; n: number; box: THREE.Box3 }>();
    const p = new THREE.Vector3();
    for (const v of kinds.keys()) {
      const r = find(v);
      const c = centre.get(r) ?? { x: 0, y: 0, z: 0, n: 0, box: new THREE.Box3() };
      p.fromBufferAttribute(pos, v);
      c.x += p.x; c.y += p.y; c.z += p.z; c.n += 1;
      c.box.expandByPoint(p);
      centre.set(r, c);
    }
    for (const [v, kind] of kinds) {
      if (kind === "lens") { glow[v] = 2; continue; }
      const c = centre.get(find(v));
      if (!c) continue;
      const seed = 0.02 + 0.96 * hash3(Math.floor(c.x / c.n / 0.25), Math.floor(c.y / c.n / 0.25), Math.floor(c.z / c.n / 0.25));
      const size = c.box.getSize(p);
      const big = Math.max(size.y, Math.hypot(size.x, size.z)) > 0.3 ? 3 : 0;
      glow[v] = kind === "shop" ? -(seed + big) : seed + big;
    }
  }
  out.setAttribute("nightGlow", new THREE.BufferAttribute(glow, 1));
  return out;
}

/**
 * The kit materials' night term: only the city's BatchedMesh draws carry `nightGlow` (USE_BATCHING — three defines it in
 * the vertex shader only, so the fragment side reads plain flat varyings), so the same material on a plain mesh (an
 * obstacle, the skyline) compiles without the attribute and never glows. The instance id varies
 * which windows are lit from building to building.
 */
function patchKitMaterial(material: THREE.Material, night: THREE.IUniform<number>): void {
  if (material.userData.nightPatched) return;
  material.userData.nightPatched = true;
  const previous = material.onBeforeCompile;
  material.onBeforeCompile = (shader, renderer) => {
    previous.call(material, shader, renderer);
    shader.uniforms.uNight = night;
    shader.vertexShader = shader.vertexShader
      .replace("#include <common>", `#include <common>
#ifdef USE_BATCHING
attribute float nightGlow;
#endif
flat varying float vNightGlow;
flat varying float vNightInst;
varying vec3 vNightLocal;`)
      .replace("#include <begin_vertex>", `#include <begin_vertex>
	vNightLocal = transformed;
#ifdef USE_BATCHING
	vNightGlow = nightGlow;
	vNightInst = getIndirectIndex( gl_DrawID );
#else
	vNightGlow = 0.0;
	vNightInst = 0.0;
#endif`);
    shader.fragmentShader = shader.fragmentShader
      .replace("#include <common>", `#include <common>
uniform float uNight;
flat varying float vNightGlow;
flat varying float vNightInst;
varying vec3 vNightLocal;
float nightHash(float n) { return fract(sin(n) * 43758.5453123); }`)
      .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
	if (uNight > 0.0 && vNightGlow != 0.0) {
		vec3 nightLight;
		if (vNightGlow > 1.5 && vNightGlow < 2.5) nightLight = vec3(1.0, 0.72, 0.42) * 3.0;  // a street lamp's lens
		else {
			float g = abs(vNightGlow);
			float big = step(2.5, g);
			float seed = g - 3.0 * big;
			float shop = step(vNightGlow, 0.0);
			float pick = nightHash(seed * 91.7 + vNightInst * 13.31);
			float frame = 1.0;
			if (big > 0.5) {
				// one quad for a whole facade: panes on a grid (model units; a kit tile is 1), each lit or not, dark mullions between
				vec2 q = vec2((vNightLocal.x + vNightLocal.z) / 0.19, vNightLocal.y / mix(0.16, 0.24, shop));
				vec2 cell = floor(q), f = fract(q);
				frame = step(0.12, f.x) * step(f.x, 0.88) * step(0.14, f.y) * step(f.y, 0.86);
				pick = nightHash(dot(cell, vec2(12.9898, 78.233)) + seed * 37.1 + vNightInst * 3.31);
			}
			float tint = nightHash(pick * 17.3 + seed * 5.1);
			float lit = step(pick, mix(0.38, 0.45, shop)) * frame;
			vec3 warm = mix(vec3(1.0, 0.6, 0.26), vec3(1.0, 0.82, 0.55), tint);
			vec3 color = tint > 0.88 ? vec3(0.42, 0.66, 1.0) : warm;
			nightLight = color * lit * mix(1.45, 0.8, shop);
		}
		totalEmissiveRadiance += nightLight * uNight;
	}`);
  };
  const key = material.customProgramCacheKey.bind(material);
  material.customProgramCacheKey = () => `${key()}|night-kit`;
}

// ------------------------------------------------------------------------------------------------ additive light fx

const FX_VERTEX = /* glsl */`
  attribute float part;
  uniform vec2 fogRange;
  varying vec2 vUv;
  varying float vPart;
  varying float vFade;
  varying vec3 vColor;
  varying float vCore;
  void main() {
    vUv = uv;
    #ifdef USE_INSTANCING_COLOR
      vColor = instanceColor;
    #else
      vColor = vec3(1.0);
    #endif
    // halo quads (part 2) keep their size but face the camera; pools and cones are placed as they are. In the moving-lights
    // field every quad is part 2: one laid flat by its instance matrix (its y axis off the vertical) is a pool
    float kind = part;
    if (kind > 1.5 && abs(instanceMatrix[1].y) < 1e-5) kind = 0.0;
    vPart = kind;
    vec4 mv;
    if (kind > 1.5) {
      vec4 centre = modelViewMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);
      float size = length(vec3(instanceMatrix[0]));
      vCore = length(vec3(instanceMatrix[2]));
      centre.z += size * 0.45;                                 // in front of the lamp it belongs to: no hard cut
      mv = centre + vec4(position.xy * size, 0.0, 0.0);
    } else {
      mv = modelViewMatrix * instanceMatrix * vec4(position, 1.0);
      vCore = 0.0;
    }
    float d = -mv.z;
    vFade = (1.0 - smoothstep(fogRange.x, fogRange.y, d)) * smoothstep(1.0, 6.0, d);
    gl_Position = projectionMatrix * mv;
  }`;

const FX_FRAGMENT = /* glsl */`
  uniform float strength;
  uniform float cones;
  varying vec2 vUv;
  varying float vPart;
  varying float vFade;
  varying vec3 vColor;
  varying float vCore;
  void main() {
    vec2 p = vUv * 2.0 - 1.0;
    float r = dot(p, p);
    float a;
    if (vPart < 0.5) a = exp(-r * 3.0) * 0.55 * (1.0 - smoothstep(0.7, 1.0, r));                   // a pool on the ground
    else if (vPart < 1.5) a = pow(clamp(vUv.y, 0.0, 1.0), 2.2) * 0.075 * cones;                      // a cone, brightest at its lamp
    else a = (exp(-r * 7.0) * 0.5 + exp(-r * 40.0) * 1.2 * vCore) * (1.0 - smoothstep(0.8, 1.0, r));    // a halo
    gl_FragColor = vec4(vColor * a * strength * vFade, 1.0);
  }`;

function fxMaterial(): THREE.ShaderMaterial {
  return new THREE.ShaderMaterial({
    uniforms: { fogRange: { value: new THREE.Vector2(50, 330) }, strength: { value: 1 }, cones: { value: 1 } },
    vertexShader: FX_VERTEX,
    fragmentShader: FX_FRAGMENT,
    transparent: true,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    side: THREE.DoubleSide,
    toneMapped: false,
    fog: false
  });
}

/** A quad in the xy plane with the `part` attribute. */
function quad(part: number): THREE.BufferGeometry {
  const g = new THREE.PlaneGeometry(1, 1);
  g.setAttribute("part", new THREE.BufferAttribute(new Float32Array(g.getAttribute("position").count).fill(part), 1));
  return g;
}

/** A flat rectangle on the ground (y = `y`) from x0 to x1, z ±r, its uv a radial falloff centred on x = 0, z = 0 (radius r). */
function poolPiece(x0: number, x1: number, r: number, y: number): THREE.BufferGeometry {
  const g = new THREE.BufferGeometry();
  const xs = [x0, x1, x1, x0], zs = [-r, -r, r, r];
  const position: number[] = [], uv: number[] = [];
  for (let i = 0; i < 4; i++) { position.push(xs[i], y, zs[i]); uv.push(xs[i] / r * 0.5 + 0.5, zs[i] / r * 0.5 + 0.5); }
  g.setAttribute("position", new THREE.Float32BufferAttribute(position, 3));
  g.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute("normal", new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], 3));
  g.setIndex([0, 2, 1, 0, 3, 2]);
  g.setAttribute("part", new THREE.BufferAttribute(new Float32Array(4).fill(0), 1));
  return g;
}

/** The pieces of one street lamp's light, in the lamp's frame (origin = the lens, +x = away from the road on the +x side):
 *  pools and halo first, the cone last — `userData.withoutCone` is the index count low draws (its cone is never rasterized). */
function lampGeometry(lensX: number, lensY: number, roadEdge: number, walkEdge: number): THREE.BufferGeometry {
  const R = 6.5;
  const parts: THREE.BufferGeometry[] = [];
  parts.push(poolPiece(-R, roadEdge - lensX, R, 0.03 - lensY));                 // on the road
  parts.push(poolPiece(walkEdge - lensX, R, R, 0.37 - lensY));                  // on the sidewalk (0.34 m up)
  parts.push(quad(2).scale(2.8, 2.8, 1));                                       // the halo (faces the camera: FX_VERTEX)
  const withoutCone = parts.reduce((n, g) => n + (g.index?.count ?? g.getAttribute("position").count), 0);
  const height = lensY - 0.3;
  const cone = new THREE.CylinderGeometry(0.22, 3.4, height, 18, 1, true).translate(0, -height / 2 - 0.05, 0);
  cone.setAttribute("part", new THREE.BufferAttribute(new Float32Array(cone.getAttribute("position").count).fill(1), 1));
  parts.push(cone);
  const merged = mergeAll(parts);
  merged.userData.withoutCone = withoutCone;
  parts.forEach((g) => g.dispose());
  return merged;
}

/** mergeGeometries for our few pieces (position, uv, part; normals dropped) — kept local so the module stays small. */
function mergeAll(parts: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const position: number[] = [], uv: number[] = [], part: number[] = [], index: number[] = [];
  let base = 0;
  for (const g of parts) {
    const p = g.getAttribute("position"), t = g.getAttribute("uv"), k = g.getAttribute("part");
    for (let i = 0; i < p.count; i++) { position.push(p.getX(i), p.getY(i), p.getZ(i)); uv.push(t.getX(i), t.getY(i)); part.push(k.getX(i)); }
    const idx = g.index;
    if (idx) for (let i = 0; i < idx.count; i++) index.push(base + idx.getX(i));
    else for (let i = 0; i < p.count; i++) index.push(base + i);
    base += p.count;
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute("position", new THREE.Float32BufferAttribute(position, 3));
  out.setAttribute("uv", new THREE.Float32BufferAttribute(uv, 2));
  out.setAttribute("normal", new THREE.Float32BufferAttribute(new Array(position.length).fill(0).map((_, i) => (i % 3 === 1 ? 1 : 0)), 3));
  out.setAttribute("part", new THREE.Float32BufferAttribute(part, 1));
  out.setIndex(index);
  return out;
}

// ------------------------------------------------------------------------------------------------ the mode

/** What GameScene hands over once: the lights, sky and materials night changes. */
export interface NightTargets {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  sun: THREE.DirectionalLight;
  hemi: THREE.HemisphereLight;
  /** the sky's colour uniforms (top, horizon, sun colour/direction) and its `night` switch */
  sky: THREE.ShaderMaterial;
  /** the haze colour shared by the fog, the sky's horizon band and the clear colour (GameScene.syncHaze re-derives the fog from it) */
  haze: THREE.Color;
  clouds: THREE.Material;
  /** the day's PMREM environment (PBR props) — the night one is made on the first night */
  makeEnvironment: (colors: { top: THREE.Color; horizon: THREE.Color; ground: THREE.Color; sun: THREE.Color }) => THREE.Texture;
  /** where the lens of a +x lamp sits on its slice (scene space, z relative to the slice; a −x lamp is its mirror image) */
  lens: { x: number; y: number; z: number };
  /** the road's edge and the sidewalk's start (|x|, metres) */
  roadEdge: number;
  walkEdge: number;
}

/** What GameScene passes every frame. */
export interface NightFrame {
  dt: number;
  clock: number;
  quality: "high" | "low";
  /** city slices: `cz` (city space) and whether a lamp pair stands on it */
  segments: readonly THREE.Object3D[];
  cityScroll: number;
  /** the lamp pair's |x| on a lamp slice */
  lampX: number;
  entities: readonly WorldEntity[];
  carZ: number;
  /** world x is mirrored on the way to the scene */
  vx: number;
  car?: THREE.Object3D;
  carBody?: THREE.Object3D;
  carLength: number;
  ground: number;
  boosters: readonly THREE.Object3D[];
  jets: readonly THREE.Object3D[];
  chaser?: THREE.Object3D;
  /** billboard roots (easterEggs.ts): the game's, and a dev page's own */
  billboards: readonly THREE.Object3D[];
  running: boolean;
}

interface DayLook {
  sunColor: THREE.Color; sunIntensity: number; shadowIntensity: number;
  hemiSky: THREE.Color; hemiGround: THREE.Color; hemiIntensity: number;
  haze: THREE.Color; skyTop: THREE.Color; skyHorizon: THREE.Color; skySun: THREE.Color; skySunDirection: THREE.Vector3;
  fogNear: number; fogFar: number; environment: THREE.Texture | null; environmentIntensity: number;
  cloudColor: THREE.Color; cloudEmissive: THREE.Color; cloudEmissiveIntensity: number;
}

const NIGHT_LOOK = {
  skyTop: new THREE.Color(0x03061a),
  skyHorizon: new THREE.Color(0x231c4e),
  haze: new THREE.Color(0x15132f),
  moon: new THREE.Color(0.78, 0.86, 1.0).multiplyScalar(0.55),
  moonDirection: new THREE.Vector3(-0.42, 0.42, 0.8).normalize(),
  moonLight: new THREE.Color(0x9fb3ff),
  moonIntensity: 0.85,
  shadowIntensity: 0.62,
  hemiSky: new THREE.Color(0x5566b0),
  hemiGround: new THREE.Color(0x2c2546),
  hemiIntensity: 0.75,
  fogNear: 45,
  fogFar: 320,
  environmentIntensity: 0.22,
  cloudColor: new THREE.Color(0x3a4270),
  cloudEmissive: new THREE.Color(0x10142c),
  bloom: 0.72
};

const LAMP_COLOR = new THREE.Color(1.0, 0.6, 0.26);
const MAX_LAMPS = 64;
const MAX_GLOWS = 200;

export class NightMode {
  /** 0 by day, 1 at night: every night-only shader term is multiplied by it */
  readonly uniform: THREE.IUniform<number> = { value: 0 };
  mode: TimeOfDay = "day";
  /** the bloom strength the post pass should use (GameScene hands it to PostFX) */
  bloom = 0.55;
  private day: DayLook;
  private nightEnvironment?: THREE.Texture;
  private lamps: THREE.InstancedMesh;
  private glows: THREE.InstancedMesh;
  private carBody?: THREE.Object3D;
  private patched = new Set<THREE.Material>();
  private flames = new Map<THREE.MeshBasicMaterial, THREE.Color>();
  private boardMaterial?: THREE.MeshLambertMaterial;
  private copFlashes: THREE.Object3D[] = [];
  private copScales = new Map<THREE.Object3D, THREE.Vector3>();
  private matrix = new THREE.Matrix4();
  private q = new THREE.Quaternion();
  private v = new THREE.Vector3();
  private s = new THREE.Vector3();
  private c = new THREE.Color();
  private flat = new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI / 2);
  private none = new THREE.Quaternion();
  private glowCount = 0;

  constructor(private t: NightTargets) {
    const fog = t.scene.fog as THREE.Fog;
    const sky = t.sky.uniforms;
    const clouds = t.clouds as THREE.MeshLambertMaterial;
    this.day = {
      sunColor: t.sun.color.clone(), sunIntensity: t.sun.intensity, shadowIntensity: t.sun.shadow.intensity,
      hemiSky: t.hemi.color.clone(), hemiGround: t.hemi.groundColor.clone(), hemiIntensity: t.hemi.intensity,
      haze: t.haze.clone(), skyTop: (sky.top.value as THREE.Color).clone(), skyHorizon: (sky.horizon.value as THREE.Color).clone(),
      skySun: (sky.sunColor.value as THREE.Color).clone(), skySunDirection: (sky.sunDirection.value as THREE.Vector3).clone(),
      fogNear: fog.near, fogFar: fog.far, environment: t.scene.environment, environmentIntensity: t.scene.environmentIntensity,
      cloudColor: clouds.color.clone(), cloudEmissive: clouds.emissive.clone(), cloudEmissiveIntensity: clouds.emissiveIntensity
    };
    // the lamps: one instance per lamp, mirrored for the −x side
    const lampMaterial = fxMaterial();
    (lampMaterial.uniforms.strength as THREE.IUniform<number>).value = 1;
    this.lamps = new THREE.InstancedMesh(lampGeometry(t.lens.x, t.lens.y, t.roadEdge, t.walkEdge), lampMaterial, MAX_LAMPS);
    this.lamps.name = "night-lamps";
    this.lamps.frustumCulled = false;
    this.lamps.count = 0;
    this.lamps.visible = false;
    this.lamps.renderOrder = 2;
    this.lamps.setColorAt(0, LAMP_COLOR);
    // everything else that shines: one field of soft quads (pools lie flat, halos face the camera)
    // (pools are the same quad laid flat by their instance matrix: FX_VERTEX tells them apart)
    this.glows = new THREE.InstancedMesh(quad(2), fxMaterial(), MAX_GLOWS);
    this.glows.name = "night-glows";
    this.glows.frustumCulled = false;
    this.glows.count = 0;
    this.glows.visible = false;
    this.glows.renderOrder = 2;
    this.glows.setColorAt(0, LAMP_COLOR);
    // (no beam cones on the roadster: seen from the chase camera a cone of light reads as smoke — its pools on the road are the headlights)
    t.scene.add(this.lamps, this.glows);
  }

  /** The kit materials of the city's batches get the night term (before their first compile). */
  patchKit(material: THREE.Material): void {
    if (this.patched.has(material)) return;
    this.patched.add(material);
    patchKitMaterial(material, this.uniform);
  }

  /** The billboards' shared material: neon brighter, boards washed by their lamps at night (before its first compile). */
  patchBillboards(root: THREE.Object3D): void {
    const mesh = root.children.find((o) => (o as THREE.Mesh).isMesh) as THREE.Mesh | undefined;
    const material = mesh?.material as THREE.MeshLambertMaterial | undefined;
    if (!material || material.userData.nightPatched) return;
    material.userData.nightPatched = true;
    this.boardMaterial = material;
    const night = this.uniform;
    material.onBeforeCompile = (shader) => {
      shader.uniforms.uNight = night;
      shader.vertexShader = shader.vertexShader
        .replace("#include <common>", "#include <common>\nvarying float vSignY;")
        .replace("#include <begin_vertex>", "#include <begin_vertex>\n\tvSignY = position.y;");
      shader.fragmentShader = shader.fragmentShader
        .replace("#include <common>", "#include <common>\nuniform float uNight;\nvarying float vSignY;")
        .replace("#include <emissivemap_fragment>", `#include <emissivemap_fragment>
	totalEmissiveRadiance *= 1.0 + 0.85 * uNight;
	totalEmissiveRadiance += diffuseColor.rgb * uNight * (0.12 + 0.95 * smoothstep(3.9, 7.4, vSignY));`);
    };
    material.customProgramCacheKey = () => "billboards-night";
  }

  /** The ELON pickup: its flash sprites grow at night (found by name, see props/cop.ts). */
  setChaser(root: THREE.Object3D): void {
    this.copFlashes = [];
    root.traverse((o) => { if (o.name.endsWith("Flash")) { this.copFlashes.push(o); this.copScales.set(o, o.scale.clone()); } });
  }

  /** Day or night, applied at once (uniforms, light colours, fog; no shader compiles). */
  set(mode: TimeOfDay, quality: "high" | "low"): void {
    this.mode = mode;
    const night = mode === "night";
    const t = this.t, d = this.day;
    const fog = t.scene.fog as THREE.Fog;
    const sky = t.sky.uniforms;
    const clouds = t.clouds as THREE.MeshLambertMaterial;
    this.uniform.value = night ? 1 : 0;
    sky.night.value = night ? 1 : 0;
    if (night) {
      t.sun.color.copy(NIGHT_LOOK.moonLight); t.sun.intensity = NIGHT_LOOK.moonIntensity; t.sun.shadow.intensity = NIGHT_LOOK.shadowIntensity;
      t.hemi.color.copy(NIGHT_LOOK.hemiSky); t.hemi.groundColor.copy(NIGHT_LOOK.hemiGround); t.hemi.intensity = NIGHT_LOOK.hemiIntensity;
      t.haze.copy(NIGHT_LOOK.haze);
      (sky.top.value as THREE.Color).copy(NIGHT_LOOK.skyTop); (sky.horizon.value as THREE.Color).copy(NIGHT_LOOK.skyHorizon);
      (sky.sunColor.value as THREE.Color).copy(NIGHT_LOOK.moon); (sky.sunDirection.value as THREE.Vector3).copy(NIGHT_LOOK.moonDirection);
      fog.near = NIGHT_LOOK.fogNear; fog.far = NIGHT_LOOK.fogFar;
      this.nightEnvironment ??= t.makeEnvironment({ top: new THREE.Color(0x0a1030), horizon: new THREE.Color(0x2a2456), ground: new THREE.Color(0x1a1626), sun: new THREE.Color(0xb8c8ff).multiplyScalar(0.8) });
      t.scene.environment = this.nightEnvironment;
      t.scene.environmentIntensity = NIGHT_LOOK.environmentIntensity;
      clouds.color.copy(NIGHT_LOOK.cloudColor); clouds.emissive.copy(NIGHT_LOOK.cloudEmissive);
      this.bloom = NIGHT_LOOK.bloom;
    } else {
      t.sun.color.copy(d.sunColor); t.sun.intensity = d.sunIntensity; t.sun.shadow.intensity = d.shadowIntensity;
      t.hemi.color.copy(d.hemiSky); t.hemi.groundColor.copy(d.hemiGround); t.hemi.intensity = d.hemiIntensity;
      t.haze.copy(d.haze);
      (sky.top.value as THREE.Color).copy(d.skyTop); (sky.horizon.value as THREE.Color).copy(d.skyHorizon);
      (sky.sunColor.value as THREE.Color).copy(d.skySun); (sky.sunDirection.value as THREE.Vector3).copy(d.skySunDirection);
      fog.near = d.fogNear; fog.far = d.fogFar;
      t.scene.environment = d.environment;
      t.scene.environmentIntensity = d.environmentIntensity;
      clouds.color.copy(d.cloudColor); clouds.emissive.copy(d.cloudEmissive); clouds.emissiveIntensity = d.cloudEmissiveIntensity;
      this.bloom = 0.55;
    }
    for (const [material, base] of this.flames) material.color.copy(base).multiplyScalar(night ? 1.6 : 1);
    for (const [sprite, scale] of this.copScales) sprite.scale.copy(scale).multiplyScalar(night ? 1.9 : 1);
    this.lamps.visible = night;
    this.glows.visible = night;
    // low: the cones are not drawn at all (fill rate), high: the whole lamp
    this.lamps.geometry.setDrawRange(0, quality === "high" ? Infinity : this.lamps.geometry.userData.withoutCone as number);
  }

  /** The roadster's flames burn hotter at night (their materials, found once per car). */
  private watchCar(body: THREE.Object3D | undefined, flames: readonly THREE.Object3D[]): void {
    if (!body || this.carBody === body) return;
    this.carBody = body;
    for (const flame of flames) {
      const material = (flame as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (material?.isMeshBasicMaterial && !this.flames.has(material)) {
        this.flames.set(material, material.color.clone());
        if (this.mode === "night") material.color.multiplyScalar(1.6);
      }
    }
  }

  /** Per frame at night: lamp instances follow the city, the moving lights follow their owners. */
  update(f: NightFrame): void {
    this.watchCar(f.carBody, [...f.boosters, ...f.jets]);
    if (this.mode !== "night") return;
    const fog = this.t.scene.fog as THREE.Fog;
    const low = f.quality === "low";
    for (const mesh of [this.lamps, this.glows]) (mesh.material as THREE.ShaderMaterial).uniforms.fogRange.value.set(fog.near, fog.far);
    // street lamps: every lamp slice on screen, both sides
    let n = 0;
    const lens = this.t.lens;
    for (const segment of f.segments) {
      if (!segment.userData.lamp) continue;
      const z = (segment.userData.cz as number) - f.cityScroll;
      if (z < -30 || z > 330) continue;
      for (const side of [-1, 1]) {
        if (n >= MAX_LAMPS) break;
        this.v.set(side * lens.x, lens.y, z + lens.z);
        this.s.set(side, 1, 1);
        this.lamps.setMatrixAt(n++, this.matrix.compose(this.v, this.none, this.s));
      }
    }
    this.lamps.count = n;
    this.lamps.instanceMatrix.needsUpdate = true;
    // the moving lights
    this.glowCount = 0;
    const halo = low ? 0.75 : 1;
    if (f.car && f.carBody) {
      const car = f.car.position;
      const lift = Math.max(0, car.y - f.ground);
      const beam = THREE.MathUtils.clamp(1 - lift / 7, 0, 1);
      if (beam > 0.02) for (const dx of [-0.8, 0.8]) this.pool(car.x + dx, f.ground + 0.04, car.z + 8.5, 2.9, 14, this.c.setRGB(1.0, 0.93, 0.8).multiplyScalar(0.17 * beam));
      // tail lights (behind the car, towards the camera), in the body's frame so they flip with it
      for (const x of [-0.72, 0.72]) {
        this.v.set(x, 0.62, -2.3).applyMatrix4(f.carBody.matrixWorld);
        this.halo(this.v.x, this.v.y, this.v.z, 0.9 * halo, this.c.setRGB(1.0, 0.06, 0.08).multiplyScalar(1.3), 1);
      }
      for (const flame of f.boosters) {
        const power = flame.visible ? Math.min(1.4, flame.scale.y) : 0;
        if (power < 0.05) continue;
        flame.getWorldPosition(this.v);
        this.halo(this.v.x, this.v.y, this.v.z - 0.3, 1.6 * power * halo, this.c.setRGB(0.45, 0.85, 1.0).multiplyScalar(1.1), 1);
      }
    }
    // ETH trucks: tail lights on the parked trains, a long pool ahead of the oncoming ones
    for (const e of f.entities) {
      if (!e.active || (e.kind !== "truck" && e.kind !== "mover")) continue;
      const rel = e.z - f.carZ;
      if (rel < -20 || rel > 260) continue;
      const x = f.vx * e.x;
      if (e.kind === "mover" || e.speedZ > 0) {
        this.pool(x, 0.05, rel - 9, 5.2, 18, this.c.setRGB(1.0, 0.9, 0.72).multiplyScalar(0.9));
        for (const dx of [-1.12, 1.12]) this.halo(x + dx, 1.56, rel - 0.2, 1.5 * halo, this.c.setRGB(1.0, 0.92, 0.75).multiplyScalar(1.2), 1);
      } else {
        for (const dx of [-1.3, 1.3]) this.halo(x + dx, 1.13 + e.yBase, rel - 0.15, 1.0 * halo, this.c.setRGB(1.0, 0.07, 0.1).multiplyScalar(1.2), 1);
        this.halo(x, 5.2 + e.yBase, rel - 0.1, 0.9 * halo, this.c.setRGB(1.0, 0.45, 0.08), 0.6);
      }
    }
    // ELON: his light bar and hazards pop, and light the road towards you while they flash
    if (f.chaser?.visible) {
      let flashing = 0;
      for (const o of this.copFlashes) if (o.visible) flashing++;
      const p = f.chaser.position;
      this.pool(p.x, p.y + 0.05, p.z + 6.5, 5.5, 11, this.c.setRGB(0.8, 0.88, 1.0).multiplyScalar(0.55 + 0.35 * Math.min(1, flashing)));
      if (flashing) this.pool(p.x, p.y + 0.05, p.z - 0.5, 6.5, 8, this.c.setRGB(1.0, 0.55, 0.12).multiplyScalar(0.35));
    }
    // billboards: their three lamps (boards) and a soft light on the sidewalk under each sign
    for (const root of f.billboards) {
      for (const sign of root.children) {
        if (!sign.visible || !(sign as THREE.Mesh).isMesh) continue;
        const z = sign.position.z;
        if (z < -20 || z > 260) continue;
        const geometry = (sign as THREE.Mesh).geometry;
        if (!geometry.boundingBox) geometry.computeBoundingBox();
        const board = (geometry.boundingBox?.max.y ?? 0) > 7.2;
        if (board) {
          for (const lx of [-2.2, 0, 2.2]) {
            this.v.set(lx, 7.42, 0.66).applyMatrix4(sign.matrixWorld);
            this.halo(this.v.x, this.v.y, this.v.z, 1.1 * halo, this.c.setRGB(1.0, 0.9, 0.7).multiplyScalar(1.1), 1);
          }
        }
        this.pool(sign.position.x, 0.38, z - 1.2, board ? 7.5 : 6.5, 4.5, board ? this.c.setRGB(1.0, 0.86, 0.62).multiplyScalar(0.45) : this.c.setRGB(0.62, 0.35, 1.0).multiplyScalar(0.45));
      }
    }
    this.glows.count = this.glowCount;
    this.glows.instanceMatrix.needsUpdate = true;
    if (this.glows.instanceColor) this.glows.instanceColor.needsUpdate = true;
  }

  /** A flat pool of light on the ground: centre, size (x across, z along), colour. */
  private pool(x: number, y: number, z: number, w: number, d: number, color: THREE.Color): void {
    if (this.glowCount >= MAX_GLOWS) return;
    this.v.set(x, y, z);
    this.s.set(w, d, 0.001);                                       // z scale ≈ 0: no bright core (see FX_VERTEX: halos only)
    this.place(this.flat, color);
  }

  /** A soft halo facing the camera: centre, size, colour, how much hot core it has (0–1). */
  private halo(x: number, y: number, z: number, size: number, color: THREE.Color, core: number): void {
    if (this.glowCount >= MAX_GLOWS) return;
    this.v.set(x, y, z);
    this.s.set(size, size, Math.max(0.001, core));
    this.place(this.none, color);
  }

  private place(rotation: THREE.Quaternion, color: THREE.Color): void {
    const i = this.glowCount++;
    this.glows.setMatrixAt(i, this.matrix.compose(this.v, rotation, this.s));
    this.glows.setColorAt(i, color);
  }

  dispose(): void {
    for (const mesh of [this.lamps, this.glows]) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      (mesh.material as THREE.Material).dispose();
    }
  }
}
